import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import { GAP, CONTRACT_API_ERRORED_VPP } from '@makemydeal/dr-shared-types';
import { OFFER_TYPE } from '@makemydeal/dr-platform-types';
import { getCurrentOfferType } from '@makemydeal/dr-dash-store/selectors/offerRedux';

type VehicleProtectionContextValue = {
    isDarwinVPPEditDisabled: boolean;
    isDarwinVppEditGuardEnabled: boolean;
    allowOverrideEditing: boolean;
    setAllowOverrideEditing: (value: boolean) => void;
};

const VehicleProtectionContext = createContext<VehicleProtectionContextValue>({
    isDarwinVPPEditDisabled: false,
    isDarwinVppEditGuardEnabled: false,
    allowOverrideEditing: false,
    setAllowOverrideEditing: () => undefined
});

type VehicleProtectionProviderProps = {
    /** Pass the flyout open state so the override toggle resets on each new session */
    isOpen?: boolean;
    children: ReactNode;
};

export function VehicleProtectionProvider({ isOpen, children }: VehicleProtectionProviderProps) {
    const offerType = useSelector(getCurrentOfferType);
    const isDarwinVppEditGuardEnabled =
        useSelector(dealerSelectors.isDarwinVppEditGuardEnabled) && offerType !== OFFER_TYPE.balloon;
    const [allowOverrideEditing, setAllowOverrideEditing] = useState(false);
    const contractErrorsByEntity = useSelector(contractingSelectors.getContractErrorListByEntity);
    const hasVPPContractErrors =
        contractErrorsByEntity[GAP]?.length > 0 || contractErrorsByEntity[CONTRACT_API_ERRORED_VPP]?.length > 0;

    // Reset the override toggle every time the flyout is opened
    useEffect(() => {
        if (isOpen) {
            setAllowOverrideEditing(hasVPPContractErrors);
        }
    }, [isOpen, hasVPPContractErrors]);

    return (
        <VehicleProtectionContext.Provider
            value={{
                isDarwinVPPEditDisabled: isDarwinVppEditGuardEnabled && !allowOverrideEditing,
                isDarwinVppEditGuardEnabled,
                allowOverrideEditing,
                setAllowOverrideEditing
            }}
        >
            {children}
        </VehicleProtectionContext.Provider>
    );
}

export function useVehicleProtectionContext(): VehicleProtectionContextValue {
    return useContext(VehicleProtectionContext);
}
